<template>
  <div id="kbo-sidebar" class="kbo-sidebar--container">
    <kbo-sidebar-submenu v-if="hasAnyPermission(['services_create', 'services_edit'])">
      <kbo-sidebar-item
        v-if="hasPermission('services_create')"
        icon="fas fa-paper-plane"
        active="/services/.*"
        label="Servicios"
        :closable="false"
      />
      <template slot="items">
        <kbo-sidebar-item
          icon="fas fa-plus"
          active="never"
          submenu
          cover-close
          label="Nuevo servicio"
          badge="F3"
          @click="handleNewService"
        />
        <kbo-sidebar-item
          v-if="hasPermission('services_create')"
          icon="fas fa-file-import"
          active="never"
          submenu
          label="Importar servicios"
          @click="handleImportServices"
        />

        <kbo-sidebar-header v-if="cachedCreatingEmptyServices.length > 0">
          <span>Nuevo sin datos</span>
          <span class="close-section" @click="handleCloseEmptyNewServices">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedCreatingEmptyServices"
          :key="`e${cachedService}`"
          icon="fas fa-paper-plane"
          :active="cachedService"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          label="Nuevo servicio"
        />

        <kbo-sidebar-header v-if="cachedCreatingNotEmptyServices.length > 0">
          <span>Nuevo con datos</span>
          <span class="close-section" @click="handleCloseNotEmptyNewServices">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedCreatingNotEmptyServices"
          :key="cachedService"
          icon="fas fa-paper-plane"
          :active="cachedService"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          label="Nuevo servicio"
        />

        <kbo-sidebar-header v-if="cachedEditingLiveServices.length > 0">
          <span>En curso</span>
          <span class="close-section" @click="handleCloseLiveServices">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedEditingLiveServices"
          :key="cachedService"
          :icon="serviceIcon(cachedService)"
          :active="escapeRegex(cachedService)"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          :label="editingServiceType(cachedService)"
        />

        <kbo-sidebar-header v-if="cachedEditingBookingServices.length > 0">
          <span>Reservas</span>
          <span class="close-section" @click="handleCloseBookings">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedEditingBookingServices"
          :key="cachedService"
          :icon="serviceIcon(cachedService)"
          :active="escapeRegex(cachedService)"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          :label="editingServiceType(cachedService)"
        />

        <kbo-sidebar-header v-if="cachedEditingPeriodicServices.length > 0">
          <span>Periódicos</span>
          <span class="close-section" @click="handleClosePeriodics">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedEditingPeriodicServices"
          :key="cachedService"
          :icon="serviceIcon(cachedService)"
          :active="escapeRegex(cachedService)"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          :label="editingServiceType(cachedService)"
        />

        <kbo-sidebar-header v-if="cachedEditingHistoricServices.length > 0">
          <span>Historial</span>
          <span class="close-section" @click="handleCloseHistorics">Cerrar grupo</span>
        </kbo-sidebar-header>
        <kbo-sidebar-item
          v-for="cachedService in cachedEditingHistoricServices"
          :key="cachedService"
          :icon="serviceIcon(cachedService)"
          :active="escapeRegex(cachedService)"
          :route="cachedService"
          submenu
          :details="serviceDetails(cachedService)"
          :label="editingServiceType(cachedService)"
        />
      </template>
    </kbo-sidebar-submenu>
    <kbo-sidebar-submenu>
      <kbo-sidebar-item
        v-if="hasPermission('calls') || hasPermission('calls_stats')"
        icon="fas fa-phone-alt"
        label="Llamadas"
        active="/calls/.*"
        :closable="false"
      />
      <template slot="items">
        <kbo-sidebar-item
          v-if="hasPermission('calls')"
          icon="fas fa-list"
          route="/calls/list"
          label="Listado de llamadas"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('calls_stats')"
          icon="fas fa-chart-bar"
          route="/calls/stats"
          label="Estadísticas de llamadas"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>
    <kbo-sidebar-item
      v-if="hasPermission('bookings')"
      route="/bookings"
      icon="far fa-calendar-alt"
      label="Reservas"
    />
    <kbo-sidebar-item
      v-if="hasPermission('recurrent')"
      route="/recurrent"
      icon="fas fa-redo"
      label="Periódicos"
    />
    <kbo-sidebar-item
      v-if="hasPermission('historic')"
      route="/historic"
      icon="fas fa-list"
      label="Historial"
    />
    <kbo-sidebar-item
      v-if="hasPermission('messages')"
      icon="fas fa-envelope"
      route="/messages"
      label="Mensajes"
    />

    <kbo-sidebar-submenu
      v-if="
        hasAnyPermission([
          'manager_streets',
          'manager_zones_stops',
          'manager_delivery',
          'manager_operators',
          'manager_trip_calculator',
          'manager_driving_distance_calculator',
          'manager_shift_calendar',
        ])
      "
    >
      <kbo-sidebar-item
        icon="fas fa-magic"
        active="/manager/.*"
        :closable="false"
        label="Gestión"
      />
      <template slot="items">
        <kbo-sidebar-item
          v-if="hasPermission('manager_streets')"
          icon="fas fa-road"
          route="/manager/streets"
          label="Calles"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_zones_stops')"
          icon="fas fa-vector-square"
          route="/manager/zones-stops"
          label="Zonas y paradas"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_delivery')"
          icon="fas fa-map-signs"
          route="/manager/delivery"
          label="Reparto"
          exact
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_operators') && !haveLimitedAccess"
          icon="fas fa-headset"
          route="/manager/operators"
          label="Usuarios y operadoras"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_delivery_lists')"
          icon="checklist"
          iconType="google"
          route="/manager/delivery-lists"
          label="Listas de reparto"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_trip_calculator')"
          icon="fas fa-calculator"
          route="/manager/trip-calculator"
          label="Calculadora de precios"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_driving_distance_calculator')"
          icon="fas fa-route"
          route="/manager/driving-distance-calculator"
          label="Distancia por carretera"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('manager_shift_calendar')"
          icon="fas fa-calendar"
          route="/manager/shift-calendar"
          label="Calendario de turnos"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>

    <kbo-sidebar-submenu
      v-if="
        hasAnyPermission([
          'admin_cabbies',
          'admin_customers',
          'admin_subscribers',
          'admin_billing',
          'admin_externals',
        ])
      "
    >
      <kbo-sidebar-item icon="fas fa-archive" active="/admin/.*" :closable="false" label="Admin" />
      <template slot="items">
        <kbo-sidebar-item
          v-if="hasPermission('admin_cabbies')"
          icon="fas fa-taxi"
          route="/admin/taxis"
          label="Taxis"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('admin_customers')"
          icon="fas fa-users"
          route="/admin/customers"
          label="Clientes"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('admin_subscribers')"
          icon="fas fa-address-book"
          route="/admin/abonados"
          label="Abonados"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('admin_externals')"
          icon="fas fa-ad"
          route="/admin/externals"
          label="Externos"
          submenu
        />
        <kbo-sidebar-item
          v-if="hasPermission('admin_billing')"
          icon="fas fa-file-invoice"
          route="/admin/billing"
          label="Facturación"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>

    <!-- <kbo-sidebar-item
      v-if="hasPermission('audit')"
      :img="auditIcon"
      label="Auditoría"
      route="/audit"
    /> -->

    <kbo-sidebar-submenu v-if="hasPermission('reports')">
      <kbo-sidebar-item
        icon="fas fa-chart-bar"
        label="Informes"
        active="/reports"
        :closable="false"
      />
      <template slot="items">
        <kbo-sidebar-item
          v-if="!isNeositek"
          icon="fas fa-chart-bar"
          route="/reports"
          label="Informes"
          submenu
        />
        <kbo-sidebar-item
          icon="fas fa-clock"
          route="/reports/periodic"
          label="Informes periódicos"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>

    <kbo-sidebar-submenu v-if="hasPermission('support')">
      <kbo-sidebar-item
        icon="fas fa-ticket-alt"
        active="/support"
        :closable="false"
        label="Tickets"
      />
      <template slot="items">
        <kbo-sidebar-item
          route="/support"
          icon="fas fa-list-ul"
          label="Gestión de tickets"
          submenu
        />
        <kbo-sidebar-item
          disabled
          icon="fas fa-camera"
          label="Nuevo ticket de la vista actual"
          submenu
          @click="handleCreateTicketInContext"
        />
        <kbo-sidebar-item
          disabled
          route="/support?create=true"
          active="never"
          icon="fas fa-plus"
          label="Nuevo ticket"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>

    <kbo-sidebar-item
      v-if="hasPermission('backups') && !isNeositek"
      route="/backups"
      icon="fas fa-database"
      label="Backups"
    />

    <kbo-sidebar-item
      v-if="hasPermission('config')"
      icon="fas fa-sliders-h"
      route="/config"
      label="Configs"
    />

    <kbo-sidebar-submenu v-if="hasPermission('changelog') && !isNeositek">
      <kbo-sidebar-item icon="far fa-life-ring" active="/help/.*" :closable="false" label="Ayuda" />
      <template slot="items">
        <kbo-sidebar-item
          route="/help/changelog"
          icon="fas fa-info-circle"
          label="Novedades de la versión"
          submenu
        />
      </template>
    </kbo-sidebar-submenu>
  </div>
</template>

<script>
import KboSidebarHeader from "@/layout/components/Sidebar/KboSidebarHeader.vue";
import KboSidebarItem from "@/layout/components/Sidebar/KboSidebarItem.vue";

import KboSidebarSubmenu from "@/layout/components/Sidebar/KboSidebarSubmenu.vue";
import flavours from "@/mixins/flavours";
import { getUnixTimeInSeconds } from "@/utils/date.js";
import { parse } from "flatted";
import html2canvas from "html2canvas";
import { mapActions, mapGetters } from "vuex";
import auditIcon from "@/modules/audit/assets/icons/audit-icon.svg";

export default {
  name: "KboSidebar",

  components: {
    KboSidebarItem,
    KboSidebarSubmenu,
    KboSidebarHeader,
  },

  mixins: [flavours],

  computed: {
    ...mapGetters("app", ["version", "selectedWorkgroupsIds", "selectedCentralsIds"]),
    ...mapGetters("operators", ["hasAccessTo", "operator"]),
    ...mapGetters("persistedCache", ["persistedCachedRoutes"]),
    haveLimitedAccess() {
      return this.operator.data?.access && this.operator.data.access.length > 0;
    },
    auditIcon() {
      return auditIcon;
    },
    cachedCreatingEmptyServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/create"))
          .filter(
            (url) =>
              !this.isNewServiceNotEmpty(
                parse(this.persistedCachedRoutes[url]?.["ServiceView"]),
                url
              )
          );
      } catch (e) {
        return [];
      }
    },
    cachedCreatingNotEmptyServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/create"))
          .filter((url) =>
            this.isNewServiceNotEmpty(parse(this.persistedCachedRoutes[url]?.["ServiceView"]), url)
          );
      } catch (e) {
        return [];
      }
    },
    cachedEditingHistoricServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/edit"))
          .filter((url) => this.serviceType(url) === "historic");
      } catch (e) {
        return [];
      }
    },
    cachedEditingLiveServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/edit"))
          .filter((url) => this.serviceType(url) === "now");
      } catch (e) {
        return [];
      }
    },
    cachedEditingPeriodicServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/edit"))
          .filter((url) => this.serviceType(url) === "periodic");
      } catch (e) {
        return [];
      }
    },
    cachedEditingBookingServices() {
      try {
        return Object.keys(this.persistedCachedRoutes)
          .filter((url) => url.startsWith("/services/edit"))
          .filter((url) => this.serviceType(url) === "booking");
      } catch (e) {
        return [];
      }
    },
  },

  methods: {
    ...mapActions("app", ["setImportServicesDrawer"]),
    ...mapActions("persistedCache", ["removePersistedCachedRoute"]),
    handleNewService() {
      this.$router.push(`/services/create/${getUnixTimeInSeconds()}`);
    },
    hasAnyPermission(routes) {
      for (let i = 0; i < routes.length; i++) {
        if (this.hasPermission(routes[i])) return true;
      }
      return false;
    },
    hasPermission(route) {
      return this.hasAccessTo(route);
    },
    escapeRegex(text) {
      return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    },
    serviceIcon(service) {
      const serviceType = this.serviceType(service);
      switch (serviceType) {
        case "now":
          return "fas fa-list";
        case "historic":
          return "fas fa-list";
        case "booking":
          return "far fa-calendar-alt";
        case "periodic":
          return "fas fa-redo";
        default:
          return "fas fa-paper-plane";
      }
    },
    serviceType(service) {
      const entry = this.persistedCachedRoutes?.[service]?.ServiceView;
      if (!entry) {
        return null;
      }
      return parse(entry)?.editingServiceTypeValue || null;
    },
    serviceDetails(service) {
      const entry = this.persistedCachedRoutes?.[service]?.ServiceView;
      if (entry) {
        const data = parse(entry);
        const phone = data.customerData?.phone || "Sin teléfono";
        return `${phone}`;
      }
      return "Sin teléfono";
    },
    editingServiceType(service) {
      const serviceType = this.serviceType(service);
      switch (serviceType) {
        case "historic":
          return "Histórico";
        case "booking":
          return "Reserva";
        case "periodic":
          return "Periódico";
        case "now":
          return "En curso";
        default:
          return serviceType;
      }
    },
    isNewServiceNotEmpty(service, route) {
      return (
        service?.routeData?.from?.street ||
        service?.routeData?.to?.street ||
        service?.scheduleData?.mode !== "now" ||
        service?.propertiesData?.taxi ||
        service?.subscriberData?.subscriber ||
        Object.values(service?.stagesData?.stages).filter((s) => s !== "").length > 0 ||
        (service?.customerData?.id && !route.includes("asteriskId"))
      );
    },
    handleCloseEmptyNewServices() {
      this.cachedCreatingEmptyServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseNotEmptyNewServices() {
      this.cachedCreatingNotEmptyServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseLiveServices() {
      this.cachedEditingLiveServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseBookings() {
      this.cachedEditingBookingServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleClosePeriodics() {
      this.cachedEditingPeriodicServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseHistorics() {
      this.cachedEditingHistoricServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleImportServices() {
      this.setImportServicesDrawer(true);
    },
    closeIfCurrentRoute(route) {
      return new Promise((resolve) => {
        if (this.$route.fullPath === route) {
          this.$router.push(
            this.$route.query.fromName ? { name: this.$route.query.fromName } : "/"
          );
          return setTimeout(resolve, 1000);
        }
        resolve();
      });
    },
    handleCreateTicketInContext() {
      html2canvas(document.body).then(async (canvas) => {
        canvas.toBlob(
          async (data) => {
            window.screenshot = data;
            window.ticketContext = {
              time: getUnixTimeInSeconds(),
              version: this.version,
              selectedWorkgroupsIds: this.selectedWorkgroupsIds,
              selectedCentralsIds: this.selectedCentralsIds,
              route: {
                name: this.$route.name,
                fullPath: this.$route.fullPath,
                path: this.$route.path,
                params: this.$route.params,
                query: this.$route.query,
              },
            };
            if (this.$route.path.startsWith("/support")) {
              this.$router.push("/", () => {
                setTimeout(() => {
                  this.$router.push("/support?create=true");
                }, 500);
              });
            } else {
              this.$router.push("/support?create=true");
            }
          },
          "image/jpg",
          0.85
        );
      });
    },
  },
};
</script>

<style lang="scss">
.kbo-sidebar--container.close {
  .kbo-sidebar--submenu .items {
    display: none !important;
  }
}
</style>

<style lang="scss" scoped>
.kbo-sidebar--container {
  position: fixed;
  z-index: 10002;
  top: var(--topbar-height);
  color: white !important;
  background-color: $menuBg2;
  bottom: 0;
  left: 0;
  width: 54px;

  .kbo-sidebar--separator {
    height: 2px;
    background-color: white;
    width: 100%;
  }
}

.close-section {
  color: white;
  cursor: pointer;
  text-decoration: underline;
}

.neositek .kbo-sidebar--container {
  background-color: $neositekMenuBg;
}
</style>
